import { Component, OnInit, ViewChild, Input, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { indicate } from 'src/app/operators';
import { EsraPipingAPIClient, BranchTableViewModel } from 'src/app/shared/models/autogenerated-piping';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap, first } from 'rxjs/operators';
import {
  faSearch,
  faTrash,
  faPen,
  faPlusCircle,
  faInfoCircle,
  faPlus,
  faEye,
  faSync,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { PageEvent, MatPaginator } from '@angular/material/paginator';
import { BranchPreviewComponent } from '../dialogs/branch-preview/branch-preview.component';
import { ServiceNoteUsageComponent } from '../dialogs/service-note-usage/service-note-usage.component';
import { DictionaryDeleteComponent } from '../dialogs/dictionary-delete/dictionary-delete.component';
import { BranchTableSelectorModalComponent } from './branch-table-selector-modal/branch-table-selector-modal.component';
import { CommonNoteSelectorModalComponent } from './common-note-selector-modal/common-note-selector-modal.component';
import { productionGuard } from 'src/app/core/guards/guards';
import { MatTableDataSource } from '@angular/material/table';
import { InsightsService } from 'src/app/shared/services/insights.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { BranchTableSyncService, BranchTableValidationResult } from 'src/app/shared/services/branch-table-sync.service';
import { DictionaryExportService } from '../services/dictionary-export.service';
import { BranchCreateComponent } from '../dialogs/branch-create/branch-create.component';

@Component({
    selector: 'app-branch',
    templateUrl: './branch.component.html',
    styleUrls: ['./branch.component.scss'],
    standalone: false
})
export class BranchComponent implements OnInit {
  @ViewChild('paginator') paginator: MatPaginator;

  // Input for modal mode
  @Input() isModalMode: boolean = false;
  @Input() excludeIds: string[] = [];
  @Input() existingSelectedTables: BranchTableViewModel[] = [];

  // Output for table selection in modal mode
  @Output() tableSelectionChange = new EventEmitter<BranchTableViewModel[]>();

  constructor(
    private esraApiClient: EsraPipingAPIClient,
    private snackBarService: SnackBarService,
    private insightsService: InsightsService,
    private dialog: MatDialog,
    private branchTableSyncService: BranchTableSyncService,
    private cdr: ChangeDetectorRef,
    private dictionaryExportService: DictionaryExportService
  ) {}

  /** Export function passed to the shared export button component. */
  exportFn = () => this.dictionaryExportService.exportBranchTables();

  searching$ = new BehaviorSubject<boolean>(false);
  tableBranchArray: BranchTableViewModel[] = [];
  searchText = '';
  faEdit = faPen;
  faTrashCan = faTrash;
  faSearch = faSearch;
  faPlusCircle = faPlusCircle;
  faInfo = faInfoCircle;
  faPlus = faPlus;
  faEye = faEye;
  faSync = faSync;
  faSpinner = faSpinner;

  // Branch Tables functionality - receive edit permission from parent
  @Input() canEdit$: Observable<boolean>;
  selectedBranchTables: any[] = [];
  newFreeTextNote: { [key: string]: string } = {};
  comments: { text: string; date: Date }[] = [];
  newComment = '';
  globalCommonNotes: any[] = [];

  currentPage = 0;
  pageSize = 100;
  totalCount: number;
  pageSizes = [2, 5, 10, 25, 50, 100];

  columnNames: string[] = ['NAME', 'PREVIEW', 'STATUS'];
  newBranchTable: BranchTableViewModel | null = null;
  dataSource = new MatTableDataSource<BranchTableViewModel>(this.tableBranchArray);

  // Selection functionality for modal mode
  selectedTablesForModal: BranchTableViewModel[] = [];
  selectAll = false;

  // Branch Table Sync functionality
  branchTableSyncStates: Map<number, BranchTableValidationResult> = new Map();
  branchTableSyncLoading: Map<number, boolean> = new Map();
  private syncInitialized = false;

  ngOnInit() {
    // Configure columns based on mode
    if (this.isModalMode) {
      // In modal mode, hide STATUS toggle - only show selection, name, and preview
      this.columnNames = ['SELECT', 'NAME', 'PREVIEW'];

      // Initialize empty selection (modal now only shows tables that are NOT already selected)
      this.selectedTablesForModal = [];
      this.selectAll = false;
    } else {
      if (productionGuard()) {
        this.columnNames.push('actions');
      }
    }

    this.populateTableBranchArray();

    if (!this.isModalMode) {
      this.insightsService.logPageView('branchTableDictionary ngOnInit');
    }
  }

  populateTableBranchArray() {
    this.esraApiClient
      .getCurrentBranchTablesByPageNumberAndNumberOfResults(
        Number(this.currentPage),
        Number(this.pageSize),
        this.searchText
      )
      .pipe(
        indicate(this.searching$),
        first(),
        tap((searchResult) => {
          this.totalCount = searchResult.totalResults;
          this.tableBranchArray = searchResult.results;

          // If in modal mode, filter out excluded tables and show only active ones
          if (this.isModalMode) {
            const filteredTables = this.tableBranchArray.filter(table => {
              const isNotExcluded = table.branchTableId && !this.excludeIds.includes(table.branchTableId);
              const isActive = table.isActive === true;
              return isNotExcluded && isActive;
            });

            this.dataSource.data = filteredTables;
            this.updateSelectAllState();
          } else {
            this.dataSource.data = this.tableBranchArray;
          }
        })
      )
      .subscribe();
  }

  public previewBranch(table: BranchTableViewModel) {
    this.dialog.open(BranchPreviewComponent, {
        minWidth: '60vw',
        minHeight: '50vh',
        maxWidth: '90vw',
        maxHeight: '80vh',
      data: table,
    });
  }

  public createNewBranchTable(): void {
    this.insightsService.logEvent('branchTableDictionary createNewBranchTableSTART');
    this.openBranchEditor(null);
  }

  public editBranch(branchTable: BranchTableViewModel): void {
    this.insightsService.logEvent('branchTableDictionary editBranchTableSTART');
    this.openBranchEditor(branchTable);
  }

  private openBranchEditor(branchTable: BranchTableViewModel | null): void {
    const dialogRef: MatDialogRef<BranchCreateComponent, boolean> = this.dialog.open<
      BranchCreateComponent,
      BranchTableViewModel | null,
      boolean
    >(BranchCreateComponent, {
      width: '98vw',
      maxWidth: '98vw',
      height: '98vh',
      maxHeight: '98vh',
      disableClose: true,
      data: branchTable
    });

    dialogRef.afterClosed().pipe(first()).subscribe((saved: boolean | undefined) => {
      if (saved) {
        this.populateTableBranchArray();
      }
    });
  }

  private addNewBranchTableToTop(): void {
    if (this.newBranchTable) {
      this.tableBranchArray = [this.newBranchTable, ...this.tableBranchArray];
      this.dataSource.data = this.tableBranchArray;
      this.newBranchTable = null;
    }
  }

  public getUsageInformation(table: BranchTableViewModel) {
    this.dialog.open(ServiceNoteUsageComponent, {
      width: '40%',
      height: '40%',
      data: table,
    });
  }

  openDeletionDialog(branchTable: BranchTableViewModel) {
    this.dialog.open(DictionaryDeleteComponent, {
      width: '40%',
      height: '23%',
      data: {
        item: branchTable,
        objectName: 'branch table',
        objectType: 'branch table'
      }
    }).afterClosed().subscribe((result) => {
      if (result)
      {
        this.esraApiClient
        .archiveBranchTable(branchTable.branchTableId)
        .subscribe({
          next: () => {
            this.insightsService.logEvent('ArchiveBranchTable', {
              branchTable: JSON.stringify(branchTable)
            });
            this.snackBarService.showSnackBar(
              true,
              `Branch table "${branchTable.name}" was successfully archived.`,
              'mat-snack-bar-info'
            );
            this.populateTableBranchArray();
          },
          error: (error) => {
            this.snackBarService.showSnackBar(
              true,
              `Sorry, something went wrong. Error trying to archive branch table "${branchTable.name}".`,
              'mat-snack-bar-error'
            ),
            this.insightsService.logException(error);
          }
        });
      }
    });
  }

  public clearSearchText() {
    this.searchText = '';
    this.currentPage = 0;
    this.paginator.firstPage();
    this.populateTableBranchArray();
  }

  public updatePage(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
    this.populateTableBranchArray();
  }

  protected updateStatus(branchTable: BranchTableViewModel) {
    this.esraApiClient.toggleIsActiveBranchTables(branchTable.isActive, [branchTable.branchTableId]).pipe(indicate(this.searching$), first(),
    tap(() => this.populateTableBranchArray())).subscribe({error: () => this.populateTableBranchArray()});
  }

  // Branch Tables functionality methods
  openAddBranchTableModal(): void {
    // Open modal to select branch tables from existing ones
    const dialogRef = this.dialog.open(BranchTableSelectorModalComponent, {
      width: '800px',
      height: '600px',
      data: {
        excludeIds: this.selectedBranchTables.map(bt => bt.branchTableId)
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.length > 0) {
        result.forEach((table: any) => {
          this.addBranchTable(table);
        });
      }
    });
  }

  addBranchTable(branchTable: any): void {
    const tableWithNotes = {
      ...branchTable,
      freeTextNotes: [],
      commonNoteIds: []
    };
    const newIndex = this.selectedBranchTables.length;
    this.selectedBranchTables.push(tableWithNotes);
    this.newFreeTextNote[branchTable.branchTableId] = '';

    // Validate the newly added branch table
    setTimeout(() => {
      this.validateBranchTable(newIndex);
    }, 100);
  }

  removeBranchTable(index: number): void {
    const branchTable = this.selectedBranchTables[index];

    // Remove from global common notes
    this.removeAllCommonNotesFromBranchTable(branchTable.branchTableId);

    // Remove from selected tables
    this.selectedBranchTables.splice(index, 1);
    delete this.newFreeTextNote[branchTable.branchTableId];

    // Clear sync states for this table
    this.branchTableSyncStates.delete(index);
    this.branchTableSyncLoading.delete(index);

    // Rebuild sync states map with updated indices
    const newSyncStates = new Map<number, BranchTableValidationResult>();
    const newSyncLoading = new Map<number, boolean>();

    this.branchTableSyncStates.forEach((value, key) => {
      if (key > index) {
        newSyncStates.set(key - 1, value);
      } else if (key < index) {
        newSyncStates.set(key, value);
      }
    });

    this.branchTableSyncLoading.forEach((value, key) => {
      if (key > index) {
        newSyncLoading.set(key - 1, value);
      } else if (key < index) {
        newSyncLoading.set(key, value);
      }
    });

    this.branchTableSyncStates = newSyncStates;
    this.branchTableSyncLoading = newSyncLoading;
  }

  openAddCommonNoteModal(branchTableId: string): void {
    const currentNotes = this.getCommonNotesForBranchTable(branchTableId);
    const dialogRef = this.dialog.open(CommonNoteSelectorModalComponent, {
      width: '600px',
      data: {
        excludeIds: currentNotes.map(note => note.id)
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.length > 0) {
        result.forEach((note: any) => {
          this.addCommonNoteToBranchTable(branchTableId, note);
        });
      }
    });
  }

  addCommonNoteToBranchTable(branchTableId: string, commonNote: any): void {
    const branchTable = this.selectedBranchTables.find(bt =>
      bt.branchTableId === branchTableId
    );
    if (branchTable) {
      if (!branchTable.commonNoteIds) {
        branchTable.commonNoteIds = [];
      }
      branchTable.commonNoteIds.push(commonNote.id);

      // Add to global common notes if not exists
      const existingNote = this.globalCommonNotes.find(n => n.id === commonNote.id);
      if (!existingNote) {
        this.globalCommonNotes.push(commonNote);
      }
    }
  }

  removeCommonNoteFromBranchTable(branchTableId: string, noteId: string): void {
    const branchTable = this.selectedBranchTables.find(bt =>
      bt.branchTableId === branchTableId
    );
    if (branchTable && branchTable.commonNoteIds) {
      const index = branchTable.commonNoteIds.indexOf(noteId);
      if (index > -1) {
        branchTable.commonNoteIds.splice(index, 1);

        // Check if note is used elsewhere, if not remove from global
        const isUsedElsewhere = this.selectedBranchTables.some(bt =>
          bt.branchTableId !== branchTableId && bt.commonNoteIds?.includes(noteId)
        );

        if (!isUsedElsewhere) {
          const globalIndex = this.globalCommonNotes.findIndex(n => n.id === noteId);
          if (globalIndex > -1) {
            this.globalCommonNotes.splice(globalIndex, 1);
          }
        }
      }
    }
  }

  removeAllCommonNotesFromBranchTable(branchTableId: string): void {
    const branchTable = this.selectedBranchTables.find(bt =>
      bt.branchTableId === branchTableId
    );
    if (branchTable && branchTable.commonNoteIds) {
      branchTable.commonNoteIds.forEach((noteId: string) => {
        const isUsedElsewhere = this.selectedBranchTables.some(bt =>
          bt.branchTableId !== branchTableId && bt.commonNoteIds?.includes(noteId)
        );

        if (!isUsedElsewhere) {
          const globalIndex = this.globalCommonNotes.findIndex(n => n.id === noteId);
          if (globalIndex > -1) {
            this.globalCommonNotes.splice(globalIndex, 1);
          }
        }
      });
    }
  }

  getCommonNotesForBranchTable(branchTableId: string): any[] {
    const branchTable = this.selectedBranchTables.find(bt =>
      bt.branchTableId === branchTableId
    );
    if (!branchTable || !branchTable.commonNoteIds) {
      return [];
    }

    return this.globalCommonNotes.filter(note =>
      branchTable.commonNoteIds?.includes(note.id)
    );
  }

  addFreeTextNote(branchTableId: string): void {
    const noteText = this.newFreeTextNote[branchTableId]?.trim();
    if (!noteText) return;

    const branchTable = this.selectedBranchTables.find(bt =>
      bt.branchTableId === branchTableId
    );
    if (branchTable) {
      if (!branchTable.freeTextNotes) {
        branchTable.freeTextNotes = [];
      }
      branchTable.freeTextNotes.push(noteText);
      this.newFreeTextNote[branchTableId] = '';
    }
  }

  removeFreeTextNote(branchTableId: string, index: number): void {
    const branchTable = this.selectedBranchTables.find(bt =>
      bt.branchTableId === branchTableId
    );
    if (branchTable && branchTable.freeTextNotes) {
      branchTable.freeTextNotes.splice(index, 1);
    }
  }

  addComment(): void {
    const commentText = this.newComment?.trim();
    if (!commentText) return;

    this.comments.push({
      text: commentText,
      date: new Date()
    });
    this.newComment = '';
  }

  removeComment(index: number): void {
    this.comments.splice(index, 1);
  }

  // Modal selection methods
  toggleTableSelection(table: BranchTableViewModel): void {
    if (!this.isModalMode) return;

    const index = this.selectedTablesForModal.findIndex(t => t.branchTableId === table.branchTableId);

    if (index > -1) {
      // Deselect
      this.selectedTablesForModal.splice(index, 1);
    } else {
      // Select - add from filtered dictionary tables (tables already selected are excluded from modal)
      this.selectedTablesForModal.push(table);
    }

    this.updateSelectAllState();
    this.tableSelectionChange.emit(this.selectedTablesForModal);
  }

  isTableSelected(table: BranchTableViewModel): boolean {
    if (!this.isModalMode) return false;
    const isSelected = this.selectedTablesForModal.some(t => t.branchTableId === table.branchTableId);
    return isSelected;
  }

  toggleSelectAll(): void {
    if (!this.isModalMode) return;

    if (this.selectAll) {
      // Deselect all
      this.selectedTablesForModal = [];
    } else {
      // Select all available tables from filtered dataSource (already excludes selected tables)
      this.selectedTablesForModal = [...this.dataSource.data];
    }

    this.selectAll = !this.selectAll;
    this.tableSelectionChange.emit(this.selectedTablesForModal);
  }

  private updateSelectAllState(): void {
    if (!this.isModalMode) return;

    // In modal mode, dataSource.data already contains only available tables
    const availableTables = this.dataSource.data;
    this.selectAll = availableTables.length > 0 &&
                    this.selectedTablesForModal.length === availableTables.length;
  }

  shouldShowTable(table: BranchTableViewModel): boolean {
    if (!this.isModalMode) return true;

    const shouldShow = !this.excludeIds.includes(table.branchTableId);
    return shouldShow;
  }

  // Branch Table Sync Methods
  /**
   * Validates all branch tables in the selectedBranchTables array
   */
  validateAllBranchTables(): void {
    if (!this.selectedBranchTables || this.selectedBranchTables.length === 0) {
      return;
    }

    this.selectedBranchTables.forEach((_, index) => {
      if (!this.branchTableSyncStates.has(index)) {
        this.validateBranchTable(index);
      }
    });
  }

  /**
   * Validates a branch table to check if it's the latest version
   */
  private validateBranchTable(tableIndex: number): void {
    const branchTable = this.selectedBranchTables[tableIndex];
    if (!branchTable || !branchTable.branchTableId) {
      return;
    }

    const existingState = this.branchTableSyncStates.get(tableIndex);
    if (existingState) {
      return;
    }

    this.branchTableSyncLoading.set(tableIndex, true);
    this.branchTableSyncService.validateAndGetLatestBranchTable(branchTable.branchTableId)
      .pipe(first())
      .subscribe({
        next: (result) => {
          this.branchTableSyncStates.set(tableIndex, result);
          this.branchTableSyncLoading.set(tableIndex, false);
          this.cdr.detectChanges();
        },
        error: () => {
          this.branchTableSyncLoading.set(tableIndex, false);
          const fallbackState = {
            isCurrent: true,
            message: 'Validation failed'
          };
          this.branchTableSyncStates.set(tableIndex, fallbackState);
          this.cdr.detectChanges();
        }
      });
  }

  /**
   * Checks if branch table sync is enabled (not current version)
   */
  isBranchTableSyncEnabled(tableIndex: number): boolean {
    // Validate on first check
    if (!this.branchTableSyncStates.has(tableIndex)) {
      this.validateBranchTable(tableIndex);
    }

    const syncState = this.branchTableSyncStates.get(tableIndex);
    return syncState ? !syncState.isCurrent : false;
  }

  /**
   * Gets the tooltip for branch table sync button
   */
  getBranchTableSyncTooltip(tableIndex: number): string {
    const syncState = this.branchTableSyncStates.get(tableIndex);

    if (!syncState) {
      return 'Checking for updates...';
    }

    if (this.branchTableSyncLoading.get(tableIndex)) {
      return 'Checking for updates...';
    }

    if (syncState.isCurrent) {
      return 'Branch table is up to date';
    }

    return syncState.message || 'Update to latest version available';
  }

  /**
   * Checks if branch table sync is loading
   */
  isBranchTableSyncLoading(tableIndex: number): boolean {
    return this.branchTableSyncLoading.get(tableIndex) || false;
  }

  /**
   * Syncs branch table to latest version (replaces with latest preserving notes)
   */
  syncBranchTable(tableIndex: number): void {
    const syncState = this.branchTableSyncStates.get(tableIndex);

    if (!syncState || syncState.isCurrent || !syncState.latestVersion) {
      return;
    }

    const currentBranchTable = this.selectedBranchTables[tableIndex];

    // Preserve notes from current table
    const preservedFreeTextNotes = currentBranchTable.freeTextNotes || [];
    const preservedCommonNoteIds = currentBranchTable.commonNoteIds || [];

    // Convert latest version to proper instance if needed
    const latestVersion = BranchTableViewModel.fromJS(syncState.latestVersion);

    // Create new table with latest version data and preserved notes
    const updatedTable = {
      ...latestVersion,
      freeTextNotes: preservedFreeTextNotes,
      commonNoteIds: preservedCommonNoteIds
    };

    // Replace the table
    this.selectedBranchTables[tableIndex] = updatedTable;

    // Update sync state to current
    this.branchTableSyncStates.set(tableIndex, {
      isCurrent: true,
      latestVersion: latestVersion,
      message: 'Updated to latest version with preserved notes'
    });

    // Show success message
    this.snackBarService.showSnackBar(
      true,
      `Branch table "${latestVersion.name}" updated to latest version`,
      'mat-snack-bar-success'
    );

    this.cdr.detectChanges();
  }
}
